/**
 * UI Service for providing common UI components and functionality
 */
import { reactive } from "vue";

export default {
  /**
   * Install method for Vue plugin
   * @param {Object} app - Vue application instance
   * @param {Object} options - Plugin options
   */
  install(app, options = {}) {
    // Create a reactive UI service
    const ui = reactive({
      /**
       * Toast notification system
       */
      toast: {
        /**
         * Show a toast notification
         * @param {Object} options - Toast options
         * @param {string} options.message - Message to display
         * @param {string} options.type - Type of toast (info, success, warning, error)
         * @param {number} options.duration - Duration in milliseconds
         * @param {boolean} options.queue - Whether to queue the toast
         * @returns {Object} Toast instance with close method
         */
        open(options) {
          const toast = {
            id: Date.now(),
            message: options.message || "",
            type: options.type || "info",
            duration: options.duration || 3000,
            queue: options.queue !== undefined ? options.queue : true,
            close: function () {
              // Remove toast from DOM
              const element = document.getElementById(`toast-${this.id}`);
              if (element) {
                element.classList.add("opacity-0");
                setTimeout(() => {
                  element.remove();
                }, 300);
              }
            },
          };

          // Create toast element
          const toastContainer =
            document.getElementById("toast-container") ||
            createToastContainer();
          const toastElement = document.createElement("div");
          toastElement.id = `toast-${toast.id}`;
          toastElement.className = `toast toast-${toast.type} transition-opacity duration-300`;
          toastElement.innerHTML = `
            <div class="flex items-center p-4 mb-4 text-sm rounded-lg shadow-md bg-white dark:bg-stone-800 border-l-4 ${getToastColorClass(
              toast.type
            )}">
              <span>${toast.message}</span>
              <button class="ml-auto -mx-1.5 -my-1.5 rounded-lg p-1.5 hover:bg-stone-200 dark:hover:bg-stone-700" onclick="this.parentNode.parentNode.remove()">
                <span class="sr-only">Close</span>
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                </svg>
              </button>
            </div>
          `;

          toastContainer.appendChild(toastElement);

          // Auto-close after duration
          if (toast.duration > 0) {
            setTimeout(() => {
              toast.close();
            }, toast.duration);
          }

          return toast;
        },
      },

      /**
       * Dialog system
       */
      dialog: {
        /**
         * Show an alert dialog
         * @param {Object} options - Alert options
         * @returns {Promise} Promise that resolves when dialog is closed
         */
        alert(options) {
          return new Promise((resolve) => {
            const dialog = document.createElement("div");
            dialog.className =
              "fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50";
            dialog.setAttribute("role", options.ariaRole || "dialog");
            if (options.ariaModal) {
              dialog.setAttribute("aria-modal", "true");
            }

            dialog.innerHTML = `
              <div class="bg-white dark:bg-stone-800 rounded-lg shadow-xl max-w-md w-full">
                <div class="p-6">
                  <div class="flex items-start">
                    ${options.hasIcon ? getDialogIcon(options.type) : ""}
                    <div class="ml-${options.hasIcon ? "3" : "0"}">
                      <h3 class="text-lg font-medium text-stone-900 dark:text-stone-100">${
                        options.title || "Alert"
                      }</h3>
                      <div class="mt-2 text-sm text-stone-700 dark:text-stone-300">
                        <p>${options.message || ""}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="px-6 py-3 bg-stone-50 dark:bg-stone-700 rounded-b-lg flex justify-end">
                  <button type="button" class="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                    ${options.confirmText || "OK"}
                  </button>
                </div>
              </div>
            `;

            document.body.appendChild(dialog);

            // Focus the confirm button
            setTimeout(() => {
              const confirmButton = dialog.querySelector("button");
              if (confirmButton) confirmButton.focus();
            }, 100);

            // Handle confirm button click
            dialog.querySelector("button").addEventListener("click", () => {
              document.body.removeChild(dialog);
              if (typeof options.onConfirm === "function") {
                options.onConfirm();
              }
              resolve(true);
            });
          });
        },

        /**
         * Show a confirm dialog (Vue-based via ConfirmBus)
         * @param {Object} options - Confirm options
         * @returns {Promise<boolean>} Promise that resolves with boolean result
         */
        confirm(options) {
          return new Promise(async (resolve) => {
            try {
              const { openConfirm } = await import('@/services/ui/ConfirmBus.js');
              const result = await openConfirm(options || {});
              if (result && typeof options?.onConfirm === 'function') options.onConfirm();
              if (!result && typeof options?.onCancel === 'function') options.onCancel();
              resolve(!!result);
            } catch (e) {
              // Fallback to native confirm as a last resort
              const ok = typeof window !== 'undefined' ? window.confirm(options?.message || '') : false;
              if (ok && typeof options?.onConfirm === 'function') options.onConfirm();
              if (!ok && typeof options?.onCancel === 'function') options.onCancel();
              resolve(!!ok);
            }
          });
        },

        /**
         * Show a prompt dialog with input field
         * @param {Object} options - Prompt options
         * @returns {Promise} Promise that resolves with input value or null if cancelled
         */
        prompt(options) {
          return new Promise((resolve) => {
            const inputAttrs = options.inputAttrs || {};
            const inputId = `prompt-input-${Date.now()}`;

            const dialog = document.createElement("div");
            dialog.className =
              "fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50";
            dialog.setAttribute("role", options.ariaRole || "dialog");
            if (options.ariaModal) {
              dialog.setAttribute("aria-modal", "true");
            }

            dialog.innerHTML = `
              <div class="bg-white dark:bg-stone-800 rounded-lg shadow-xl max-w-md w-full">
                <div class="p-6">
                  <div class="flex items-start">
                    ${options.hasIcon ? getDialogIcon(options.type) : ""}
                    <div class="ml-${options.hasIcon ? "3" : "0"} w-full">
                      <h3 class="text-lg font-medium text-stone-900 dark:text-stone-100">${
                        options.title || "Input Required"
                      }</h3>
                      <div class="mt-2 text-sm text-stone-700 dark:text-stone-300">
                        <p class="mb-3">${options.message || ""}</p>
                        <input 
                          type="text" 
                          id="${inputId}"
                          class="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-stone-700 dark:text-stone-100 bg-white text-stone-900"
                          placeholder="${inputAttrs.placeholder || ""}"
                          value="${inputAttrs.value || ""}"
                          maxlength="${inputAttrs.maxlength || ""}"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div class="px-6 py-3 bg-stone-50 dark:bg-stone-700 rounded-b-lg flex justify-end space-x-2">
                  <button type="button" class="cancel-btn px-4 py-2 text-sm font-medium text-stone-700 bg-white dark:bg-stone-600 dark:text-stone-200 border border-stone-300 dark:border-stone-500 rounded-md hover:bg-stone-50 dark:hover:bg-stone-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stone-500">
                    ${options.cancelText || "Cancel"}
                  </button>
                  <button type="button" class="confirm-btn px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                    ${options.confirmText || "OK"}
                  </button>
                </div>
              </div>
            `;

            document.body.appendChild(dialog);

            const inputField = dialog.querySelector(`#${inputId}`);

            // Focus the input field and select existing text
            setTimeout(() => {
              if (inputField) {
                inputField.focus();
                if (inputAttrs.value) {
                  inputField.select();
                }
              }
            }, 100);

            // Handle keyboard events
            inputField.addEventListener("keydown", (e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                dialog.querySelector(".confirm-btn").click();
              } else if (e.key === "Escape") {
                e.preventDefault();
                dialog.querySelector(".cancel-btn").click();
              }
            });

            // Handle cancel button click
            dialog
              .querySelector(".cancel-btn")
              .addEventListener("click", () => {
                document.body.removeChild(dialog);
                if (typeof options.onCancel === "function") {
                  options.onCancel();
                }
                resolve(null);
              });

            // Handle confirm button click
            dialog
              .querySelector(".confirm-btn")
              .addEventListener("click", () => {
                const value = inputField.value;
                document.body.removeChild(dialog);
                if (typeof options.onConfirm === "function") {
                  options.onConfirm(value);
                }
                resolve(value);
              });

            // Handle backdrop click to cancel
            dialog.addEventListener("click", (e) => {
              if (e.target === dialog) {
                dialog.querySelector(".cancel-btn").click();
              }
            });
          });
        },
      },

      /**
       * Loading indicator
       */
      loading: {
        /**
         * Show a loading indicator
         * @param {Object} options - Loading options
         * @returns {Object} Loading instance with close method
         */
        open(options = {}) {
          const container = options.container || document.body;
          const loading = document.createElement("div");
          loading.className =
            "fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30";
          loading.innerHTML = `
            <div class="bg-white dark:bg-stone-800 rounded-lg p-4 flex items-center space-x-3">
              <svg class="animate-spin h-5 w-5 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="text-stone-700 dark:text-stone-300">${
                options.message || "Loading..."
              }</span>
            </div>
          `;

          container.appendChild(loading);

          return {
            close: () => {
              if (loading.parentNode) {
                loading.parentNode.removeChild(loading);
              }
            },
          };
        },
      },
    });

    // Add UI service to app's global properties and window for direct access
    app.config.globalProperties.$ui = ui;
    window.$ui = ui; // Explicitly attach to window
  },
};

/**
 * Create a toast container if it doesn't exist
 * @returns {HTMLElement} Toast container element
 */
function createToastContainer() {
  const container = document.createElement("div");
  container.id = "toast-container";
  container.className = "fixed bottom-4 right-4 z-50 max-w-xs";
  document.body.appendChild(container);
  return container;
}

/**
 * Get the appropriate color class for a toast type
 * @param {string} type - Toast type
 * @returns {string} CSS class
 */
function getToastColorClass(type) {
  switch (type) {
    case "success":
      return "border-green-500 text-green-700 dark:text-green-400";
    case "warning":
      return "border-yellow-500 text-yellow-700 dark:text-yellow-400";
    case "error":
      return "border-red-500 text-red-700 dark:text-red-400";
    case "info":
    default:
      return "border-primary-500 text-primary-700 dark:text-primary-400";
  }
}

/**
 * Get the appropriate icon for a dialog type
 * @param {string} type - Dialog type
 * @returns {string} HTML for the icon
 */
function getDialogIcon(type) {
  switch (type) {
    case "is-success":
      return `
        <div class="flex-shrink-0">
          <svg class="h-6 w-6 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      `;
    case "is-warning":
      return `
        <div class="flex-shrink-0">
          <svg class="h-6 w-6 text-yellow-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
      `;
    case "is-danger":
      return `
        <div class="flex-shrink-0">
          <svg class="h-6 w-6 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      `;
    case "is-info":
    default:
      return `
        <div class="flex-shrink-0">
          <svg class="h-6 w-6 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      `;
  }
}
